import React, { memo, useEffect, useRef, useState } from 'react';
import {
  Stack,
  Card,
  Typography,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  TextField,
  Checkbox,
  Button,
  Divider,
  Icon
} from 'investira.react.components';
import { AVALIACAO_QUESTOES_RESPOSTAS } from '../../../enums';

const QUESTOES_CONFIG_POR_QUESTAO_ID = Object.values(AVALIACAO_QUESTOES_RESPOSTAS).reduce(
  (acc, respostaTipo) => {
    acc[respostaTipo.questao_id] = {
      tipo: respostaTipo.avaliacao_questao_tipo_id,
      respostas: respostaTipo.respostas || []
    };
    return acc;
  },
  {}
);

const QuestaoCardTipo = memo(props => {
  const { data, value = '', resposta = {}, onChange } = props;

  const [comentario, setComentario] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [valorTexto, setValorTexto] = useState('');
  const [valoresMultiplos, setValoresMultiplos] = useState([]);
  const inputArquivoRef = useRef(null);

  const { id, questao } = data;
  const xConfigQuestao = QUESTOES_CONFIG_POR_QUESTAO_ID[id] || {};
  const xTipoQuestao = xConfigQuestao.tipo;
  const xQuestoes = xConfigQuestao.respostas || [];
  const xRespostaSelecionada = resposta?.resposta_id ?? value ?? '';

  useEffect(() => {
    const xRespostaTexto = resposta?.resposta_texto;

    if (xTipoQuestao === 'multipla_escolha_multipla') {
      const xValores = Array.isArray(xRespostaTexto)
        ? xRespostaTexto
        : String(xRespostaTexto || '')
            .split(';')
            .map(xItem => xItem.trim())
            .filter(Boolean);
      setValoresMultiplos(xValores);
      return;
    }

    if (xTipoQuestao === 'numero' || xTipoQuestao === 'data') {
      setValorTexto(String(xRespostaTexto || ''));
    }
  }, [xTipoQuestao, resposta?.resposta_texto]);

  const handleChangeRespostaUnica = pEvent => {
    const xRespostaId = Number(pEvent.target.value);
    const xRespostaTexto = xQuestoes[xRespostaId - 1] || null;
    onChange?.(id, {
      resposta_id: xRespostaId,
      resposta_texto: xRespostaTexto
    });
  };

  const handleToggleMultipla = pValor => {
    setValoresMultiplos(pPrev => {
      const xExiste = pPrev.includes(pValor);
      const xProximos = xExiste ? pPrev.filter(xItem => xItem !== pValor) : [...pPrev, pValor];

      onChange?.(id, {
        resposta_id: xProximos.length ? 1 : null,
        resposta_texto: xProximos
      });

      return xProximos;
    });
  };

  const handleChangeTexto = pEvent => {
    const xValor = pEvent.target.value;
    setValorTexto(xValor);

    onChange?.(id, {
      resposta_id: xValor ? 1 : null,
      resposta_texto: xValor || null
    });
  };

  const getRespostaAtual = () => {
    if (xTipoQuestao === 'multipla_escolha_multipla') {
      return {
        resposta_id: valoresMultiplos.length ? 1 : null,
        resposta_texto: valoresMultiplos
      };
    }

    if (xTipoQuestao === 'numero' || xTipoQuestao === 'data') {
      return {
        resposta_id: valorTexto ? 1 : null,
        resposta_texto: valorTexto || null
      };
    }

    const xRespostaId = Number(xRespostaSelecionada);

    return {
      resposta_id: Number.isFinite(xRespostaId) && xRespostaId > 0 ? xRespostaId : null,
      resposta_texto: xQuestoes[xRespostaId - 1] || null
    };
  };

  const handleAnexarArquivo = () => {
    inputArquivoRef.current?.click();
  };

  const handleArquivoSelecionado = pEvent => {
    const xArquivosSelecionados = Array.from(pEvent.target.files || []);
    if (!xArquivosSelecionados.length) return;

    const xRespostaAtual = getRespostaAtual();

    setAnexos(pPrev => {
      const xProximosAnexos = [...pPrev, ...xArquivosSelecionados];
      onChange?.(id, {
        ...xRespostaAtual,
        comentario,
        anexos: xProximosAnexos
      });
      return xProximosAnexos;
    });

    pEvent.target.value = '';
  };

  const xTemRespostaSelecionada = Boolean(getRespostaAtual().resposta_id);

  return (
    <Card id={id}>
      <Stack gap={2} py={4}>
        <Stack px={4}>
          <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center">
              <Typography variant="caption" color="textSecondary">
                Questão {id}
              </Typography>
            </Stack>
          </Stack>
          <Stack mb={2}>
            <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 600 }}>
              {questao}
            </Typography>
          </Stack>

          {(xTipoQuestao === 'multipla_escolha_unica' ||
            xTipoQuestao === 'likert' ||
            xTipoQuestao === 'dicotomica' ||
            xTipoQuestao === 'escala_linear') && (
            <FormControl fullWidth>
              <RadioGroup
                name={`questao-${id}`}
                value={String(xRespostaSelecionada ?? '')}
                onChange={handleChangeRespostaUnica}
                row={xTipoQuestao === 'escala_linear'}>
                <Stack gap={2} direction={xTipoQuestao === 'escala_linear' ? 'row' : 'column'}>
                  {xQuestoes.map((xQuestao, xIndex) => (
                    <FormControlLabel
                      key={`questao-${id}-opcao-${xIndex + 1}`}
                      value={String(xIndex + 1)}
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2" color="textPrimary">
                          {xQuestao}
                        </Typography>
                      }
                    />
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>
          )}

          {xTipoQuestao === 'multipla_escolha_multipla' && (
            <FormControl fullWidth>
              <Stack gap={2}>
                {xQuestoes.map((xQuestao, xIndex) => (
                  <FormControlLabel
                    key={`questao-${id}-opcao-multipla-${xIndex + 1}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={valoresMultiplos.includes(xQuestao)}
                        onChange={() => handleToggleMultipla(xQuestao)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="textPrimary">
                        {xQuestao}
                      </Typography>
                    }
                  />
                ))}
              </Stack>
            </FormControl>
          )}

          {xTipoQuestao === 'numero' && (
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type="number"
              label="Resposta numérica"
              value={valorTexto}
              onChange={handleChangeTexto}
            />
          )}

          {xTipoQuestao === 'data' && (
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              type="date"
              label="Data"
              value={valorTexto}
              onChange={handleChangeTexto}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </Stack>

        <Divider />

        {xTemRespostaSelecionada && (
          <Stack px={4} gap={1.5}>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              minRows={3}
              size="small"
              label="Comentário"
              placeholder="Digite um comentário para esta questão"
              value={comentario}
              onChange={pEvent => setComentario(pEvent.target.value)}
            />
            <input
              ref={inputArquivoRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleArquivoSelecionado}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleAnexarArquivo}
                size="small"
                startIcon={<Icon iconName="paper_clip" size={16} />}>
                Anexar arquivo
              </Button>
            </Stack>
            {anexos.length > 0 && (
              <Stack gap={0.5}>
                {anexos.map((xAnexo, xIndex) => (
                  <Typography
                    key={`${xAnexo.name}-${xIndex}`}
                    variant="caption"
                    color="textSecondary">
                    {xAnexo.name}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
});

export default QuestaoCardTipo;
